import React from 'react';
import { motion } from 'framer-motion';
import { caseStudies } from '../../lib/constants';

const CaseStudyCard = ({ study, index }) => {
  return (
    <motion.div
      className="flex-shrink-0 w-[85vw] sm:w-[400px] md:w-[450px] group relative cursor-pointer"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
    >
      <div className="relative h-[500px] sm:h-[520px] md:h-[550px] rounded-3xl overflow-hidden bg-neutral-900 border border-white/10">
        <div className="absolute inset-0 group-hover:scale-105 transition-transform duration-700 ease-out">
          <img
            src={study.image}
            alt={study.client}
            className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'flex';
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center opacity-30 bg-neutral-800" style={{ display: 'none' }}>
            <span className="text-7xl sm:text-8xl md:text-9xl font-bold text-white/5 select-none">
              {study.client.charAt(0)}
            </span>
          </div>
        </div>

        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-80" />
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 z-20">
          <div className="inline-flex items-center px-3 py-1 bg-white/10 backdrop-blur-md rounded-lg border border-white/10 mb-3 md:mb-4">
            <span className="text-xs font-bold text-white uppercase tracking-wider">
              {study.client}
            </span>
          </div>

          <h3 className="text-2xl sm:text-2xl md:text-3xl font-bold text-white mb-2 leading-tight">
            {study.title}
          </h3>

          <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-[grid-template-rows] duration-500 ease-out">
            <div className="overflow-hidden">
              <p className="text-neutral-300 text-sm leading-relaxed pt-3 pb-1 border-t border-white/20 mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">
                {study.description}
              </p>
            </div>
          </div>

          <div className="flex gap-4 md:gap-6 mt-4 md:mt-6 pt-4 md:pt-6 border-t border-white/10">
            {study.metrics.slice(0, 2).map((metric, i) => (
              <div key={i}>
                <p className="text-xl md:text-2xl font-bold text-white">
                  {metric.value}{metric.suffix}
                </p>
                <p className="text-xs text-neutral-500 uppercase tracking-wide">
                  {metric.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const CaseStudies = () => {
  return (
    <section className="py-16 md:py-24 bg-black">
      <div className="container mx-auto px-4 md:px-6">
        <motion.div
          className="text-center mb-8 md:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 md:mb-4">
            Proven Results
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-neutral-400">
            We've helped startups achieve measurable growth
          </p>
        </motion.div>

        <div className="overflow-x-auto scrollbar-hide pb-8 md:pb-12 -mx-4 px-4 md:-mx-6 md:px-6">
          <motion.div
            className="flex gap-4 md:gap-8 min-w-max"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            {caseStudies.map((study, index) => (
              <CaseStudyCard key={index} study={study} index={index} />
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;